/**
 * PostHog: session replay, browser errors and clicks, sent to the "Live
 * Interviews" project. Off unless the build has REACT_APP_POSTHOG_KEY, which
 * only netlify.toml's production context sets, so deploy previews, local
 * builds and the browser suite never send anything.
 *
 * posthog-js is imported on demand: no interview feature depends on it, so it
 * stays out of the entry bundle, and a failed load never touches the call.
 * Errors reported before it has loaded are queued and sent once it has.
 */
const EARLY_EVENT_LIMIT = 20;
const CALL_PATH = /^\/(?:(?:preview|meeting|leave)\/)?([^/]+)\/([^/]+)\/?$/;

let posthogClient = null;
let initStarted = false;
const pendingExceptions = [];

export function isAnalyticsEnabled() {
  return Boolean(process.env.REACT_APP_POSTHOG_KEY);
}

/**
 * The 100ms recording bot (beam) joins every recorded interview with these
 * params and sees the video only (see PreviewScreen). Its sessions would be
 * hour-long replays of an empty grid.
 */
export function isRecordingBot(search) {
  const params = new URLSearchParams(search);
  const skipsPreview =
    params.get("skip_preview") === "true" ||
    params.get("token") === "beam_recording";
  return skipsPreview && params.get("skip_preview_headful") !== "true";
}

/** Added to every event, so replays can be found by room id and role. */
export function callProperties({ pathname, hostname }) {
  const [, roomId, role] = pathname.match(CALL_PATH) || [];
  return {
    app: "live",
    flavor: hostname.endsWith("s-core.team") ? "score" : "simbiosis",
    ...(roomId && role ? { room_id: roomId, role } : {}),
  };
}

export function captureException(error, properties) {
  if (posthogClient) {
    posthogClient.captureException(error, properties);
    return;
  }
  if (initStarted && pendingExceptions.length < EARLY_EVENT_LIMIT) {
    pendingExceptions.push([error, properties]);
  }
}

export async function startAnalytics() {
  // Once per page load, and never for the recording bot.
  if (
    !isAnalyticsEnabled() ||
    initStarted ||
    isRecordingBot(window.location.search)
  ) {
    return;
  }
  initStarted = true;

  try {
    const { default: posthog } = await import("posthog-js");
    posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
      api_host: process.env.REACT_APP_POSTHOG_HOST,
      defaults: "2026-08-30",
      person_profiles: "identified_only",
      disable_surveys: true,

      // Clicks on the elements we tag for browser tests, nothing else.
      autocapture: {
        dom_event_allowlist: ["click"],
        css_selector_allowlist: ["[data-testid]"],
      },

      // Set explicitly: both default to following the project's remote
      // config, so a toggle in PostHog would change them silently.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      enable_recording_console_log: true,

      // Names may show; inputs, chat and the candidate's profile may not
      // (data-mask). Video tiles are <video> elements, which replays never
      // capture, so nobody's camera ends up in PostHog.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-mask]",
      },
    });
    posthog.register(callProperties(window.location));
    posthogClient = posthog;
    pendingExceptions.splice(0).forEach(([error, properties]) => {
      posthog.captureException(error, properties);
    });
  } catch {
    pendingExceptions.length = 0;
  }
}
